import { FC, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Users, Star, AlertTriangle, UserX, Crown, UserPlus } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { SearchInput } from '../../components/admin/SearchInput';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Badge } from '../../components/ui/Badge';
import { useCustomerList, useCustomerSegmentCounts } from '../../hooks/admin/useAdminCustomers';
import type {
  AdminCustomerListItem,
  CustomerListQuery,
  CustomerSegment,
} from '../../types/customer.types';
import { cn } from '../../utils/cn';

const segmentConfig: Record<
  CustomerSegment,
  { label: string; icon: typeof Users; color: string; badgeVariant: 'success' | 'info' | 'warning' | 'error' | 'primary' | 'secondary' }
> = {
  NEW: { label: 'New', icon: UserPlus, color: 'bg-blue-500', badgeVariant: 'info' },
  RETURNING: { label: 'Returning', icon: Users, color: 'bg-green-500', badgeVariant: 'success' },
  LOYAL: { label: 'Loyal', icon: Star, color: 'bg-purple-500', badgeVariant: 'secondary' },
  VIP: { label: 'VIP', icon: Crown, color: 'bg-amber-500', badgeVariant: 'warning' },
  AT_RISK: { label: 'At Risk', icon: AlertTriangle, color: 'bg-orange-500', badgeVariant: 'warning' },
  CHURNED: { label: 'Churned', icon: UserX, color: 'bg-red-500', badgeVariant: 'error' },
};

const loyaltyColors: Record<string, string> = {
  BRONZE: 'bg-amber-600 text-white',
  SILVER: 'bg-gray-400 text-white',
  GOLD: 'bg-yellow-500 text-white',
  PLATINUM: 'bg-slate-700 text-white',
};

export const CustomersPage: FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState<CustomerListQuery>({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: customersData, isLoading, refetch } = useCustomerList(params);
  const { data: segmentCounts, isLoading: isLoadingCounts } = useCustomerSegmentCounts();

  const handleSearchChange = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleSegmentFilter = useCallback((segment: CustomerSegment | '') => {
    setParams((prev) => ({ ...prev, segment: segment || undefined, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const handleRowClick = useCallback(
    (customer: AdminCustomerListItem) => {
      navigate(`/admin/customers/${customer.id}`);
    },
    [navigate]
  );

  const columns: Column<AdminCustomerListItem>[] = [
    {
      key: 'name',
      header: 'Customer',
      render: (item) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-slate-100">{item.name}</div>
          <div className="text-sm text-gray-500 dark:text-slate-400">{item.email}</div>
        </div>
      ),
    },
    {
      key: 'segment',
      header: 'Segment',
      render: (item) => {
        const config = segmentConfig[item.segment];
        return <Badge variant={config.badgeVariant}>{config.label}</Badge>;
      },
    },
    {
      key: 'loyaltyTier',
      header: 'Tier',
      render: (item) => (
        <span className={cn('px-2 py-1 rounded text-xs font-medium', loyaltyColors[item.loyaltyTier])}>
          {item.loyaltyTier}
        </span>
      ),
    },
    {
      key: 'totalBookings',
      header: 'Bookings',
      render: (item) => <span>{item.totalBookings}</span>,
    },
    {
      key: 'lifetimeValue',
      header: 'Lifetime Value',
      render: (item) => <span>${item.lifetimeValue.toFixed(2)}</span>,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (item) => (
        <Badge variant={item.isActive ? 'success' : 'error'} dot>
          {item.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Customer Management</h1>
            <p className="mt-1 text-gray-500 dark:text-slate-400">View and manage customer accounts</p>
          </div>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {/* Segment Counts */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {(Object.keys(segmentConfig) as CustomerSegment[]).map((segment) => {
            const config = segmentConfig[segment];
            const Icon = config.icon;
            const count = segmentCounts?.[segment] ?? 0;
            const isActive = params.segment === segment;

            return (
              <button
                key={segment}
                onClick={() => handleSegmentFilter(isActive ? '' : segment)}
                className={cn(
                  'p-4 rounded-lg border transition-all text-left',
                  isActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-600'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn('p-2 rounded-lg', config.color)}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-slate-400">{config.label}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-slate-100">
                      {isLoadingCounts ? '-' : count}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <SearchInput
          value={params.search || ''}
          onChange={handleSearchChange}
          placeholder="Search customers by name or email..."
          className="max-w-md"
        />

        {/* Table */}
        <DataTable
          columns={columns}
          data={customersData?.data || []}
          keyExtractor={(item) => item.id}
          isLoading={isLoading}
          emptyMessage="No customers found"
          pagination={customersData?.pagination}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
        />
      </div>
    </AdminLayout>
  );
};

export default CustomersPage;
